import { useState, FormEvent } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Divider,
} from '@mui/material';
import { Add, EventBusy } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { getMyAbsences, createMyAbsence, cancelMyAbsence } from '@/api/teacher-api';
import type { AbsenceRecord } from '@timetable/types';

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  pending: 'warning',
  draft_pending: 'info',
  active: 'info',
  posted: 'success',
  cancelled: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  draft_pending: 'Being Arranged',
  active: 'Active',
  posted: 'Substitute Assigned',
  cancelled: 'Cancelled',
};

export function TeacherAbsencesPage() {
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: absences = [], isLoading, error } = useQuery({
    queryKey: ['my-absences', user?.id],
    queryFn: () => getMyAbsences(user!.id),
    enabled: !!user?.id,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelMyAbsence(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-absences', user?.id] }),
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>My Absences</Typography>
          <Typography variant="body2" color="text.secondary">View and manage your absence requests</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
          Mark Absence
        </Button>
      </Box>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Could not load absences.</Alert>}

      {!isLoading && absences.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <EventBusy sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No absences recorded yet.</Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {absences.map((absence) => (
          <Card key={absence.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[absence.status] ?? absence.status}
                      color={STATUS_COLORS[absence.status] ?? 'default'}
                    />
                    <Chip
                      size="small"
                      label={absence.absence_type === 'full_day' ? 'Full Day' : 'Temporary Duty'}
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {formatDate(absence.start_date)}
                    {absence.start_date !== absence.end_date && ` → ${formatDate(absence.end_date)}`}
                  </Typography>
                  {absence.absence_type === 'temporary_duty' && absence.start_time && (
                    <Typography variant="body2" color="text.secondary">
                      {absence.start_time?.slice(0, 5)} – {absence.end_time?.slice(0, 5)}
                    </Typography>
                  )}
                  {absence.reason && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {absence.reason}
                    </Typography>
                  )}
                </Box>
                {absence.status === 'pending' && (
                  <Button
                    size="small" color="error" variant="outlined"
                    onClick={() => cancelMutation.mutate(absence.id)}
                    disabled={cancelMutation.isPending}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <MarkAbsenceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        userId={user?.id ?? ''}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['my-absences', user?.id] });
          setDialogOpen(false);
        }}
      />
    </Box>
  );
}

// ── Mark Absence Dialog ──────────────────────────────────────────────────────

interface MarkAbsenceDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  onSuccess: () => void;
}

function MarkAbsenceDialog({ open, onClose, userId, onSuccess }: MarkAbsenceDialogProps) {
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [absenceType, setAbsenceType] = useState<'full_day' | 'temporary_duty'>('full_day');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () => createMyAbsence({
      userId,
      startDate,
      endDate: absenceType === 'temporary_duty' ? startDate : endDate,
      absenceType,
      startTime: absenceType === 'temporary_duty' ? startTime : undefined,
      endTime: absenceType === 'temporary_duty' ? endTime : undefined,
      reason: reason || undefined,
    }),
    onSuccess,
    onError: (err: Error) => setError(err.message),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (absenceType === 'temporary_duty' && (!startTime || !endTime)) {
      setError('Start and end time are required for Temporary Duty'); return;
    }
    if (absenceType === 'temporary_duty' && startTime >= endTime) {
      setError('Start time must be before end time'); return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError('End date must be on or after start date'); return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mark Absence</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <FormControl fullWidth size="small">
              <InputLabel>Absence Type</InputLabel>
              <Select value={absenceType} label="Absence Type" onChange={(e) => setAbsenceType(e.target.value as any)}>
                <MenuItem value="full_day">Full Day</MenuItem>
                <MenuItem value="temporary_duty">Temporary Duty</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Start Date" type="date" size="small" fullWidth required
              value={startDate} onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
            />

            {absenceType === 'full_day' && (
              <TextField
                label="End Date" type="date" size="small" fullWidth required
                value={endDate} onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate }}
              />
            )}

            {absenceType === 'temporary_duty' && (
              <>
                <Divider><Typography variant="caption">Time Range</Typography></Divider>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="From" type="time" size="small" fullWidth required
                    value={startTime} onChange={(e) => setStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="To" type="time" size="small" fullWidth required
                    value={endTime} onChange={(e) => setEndTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </>
            )}

            <TextField
              label="Reason (optional)" size="small" fullWidth multiline rows={2}
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Medical appointment"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}>
            {mutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
